import { IntegrationTypeSchema } from "@redotech/internal-ctx-client";
import * as z from "zod";

export const contextualIntegrationIssueCode = z.enum([
  /** Contextual received nothing from the storefront at all. Reported alone,
   * since every other check is meaningless without browser traffic. */
  "NO_BROWSER_EVENTS",
  /** Browser traffic arrived, but nothing was forwarded server-side to this
   * connected ad destination. */
  "NO_SERVER_EVENTS",
  /** Browser traffic arrived, but this ad platform's own pixel reported no
   * events, so browser-side deduplication is not happening. Only checked for
   * merchants running Contextual's tags through Google Tag Manager, since
   * that is the only path that records platform pixel events. */
  "NO_PIXEL_EVENTS",
  /** Contextual itself marked the destination as broken (rejected
   * credentials, failed deliveries). Reported for any destination type and
   * regardless of traffic, with Contextual's own message when it has one. */
  "DESTINATION_ERROR",
]);
export type ContextualIntegrationIssueCode = z.infer<
  typeof contextualIntegrationIssueCode
>;

export const contextualIntegrationIssue = z.object({
  code: contextualIntegrationIssueCode,
  /** Absent for merchant-wide issues such as NO_BROWSER_EVENTS. */
  integrationId: z.string().optional(),
  integrationType: IntegrationTypeSchema.optional(),
  integrationName: z.string().optional(),
  /** ISO time the destination was created in Contextual. Lets the UI frame
   * silence from a destination connected minutes ago as expected rather
   * than broken. */
  connectedAt: z.string().optional(),
  /** Contextual's explanation for a DESTINATION_ERROR, when it gave one. */
  statusMessage: z.string().optional(),
});
export type ContextualIntegrationIssue = z.infer<
  typeof contextualIntegrationIssue
>;

export const getContextualIntegrationHealth = {
  name: "getContextualIntegrationHealth" as const,
  input: z.object({}),
  output: z.object({
    /** The counts behind the issues cover this many days, ending now. */
    lookbackDays: z.number(),
    browserEventCount: z.number(),
    issues: z.array(contextualIntegrationIssue),
  }),
};
